<template>
  <div>
    <b-card>
      <h5 class="mb-4">Настройки трансляции</h5>
      <p class="text-muted mb-4">Управляйте основным баннером и ссылками на плееры.</p>

      <b-form @submit.prevent="saveMainBanner">
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label class="font-weight-bold">Ссылка на YouTube</label>
              <b-form-input
                v-model="currentWatch.youtube_link"
                placeholder="https://youtu.be/WxBX4N79r4w"
                :state="isYoutubeLinkValid"
              ></b-form-input>
              <b-form-invalid-feedback v-if="!isYoutubeLinkValid">
                Неверный URL YouTube
              </b-form-invalid-feedback>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label class="font-weight-bold">Ссылка на Twitch <small class="text-muted">(необязательно)</small></label>
              <b-form-input
                v-model="currentWatch.twitch_link"
                placeholder="https://twitch.tv/channel_name"
                :state="twitchValidationState"
              ></b-form-input>
              <b-form-invalid-feedback>
                Неверный URL Twitch
              </b-form-invalid-feedback>
              <small class="form-text text-muted">Если не задано — Twitch-плеер не отображается на клиенте.</small>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label class="font-weight-bold">Привязка к турниру</label>
          <div class="d-flex align-items-center">
            <b-form-input
              v-model="tournamentSearch"
              placeholder="Поиск турнира по названию..."
              class="mr-2"
              @input="onTournamentSearch"
            ></b-form-input>
            <b-button
              v-if="currentWatch.tournament_id"
              variant="outline-danger"
              size="sm"
              @click="unlinkTournament"
              title="Отвязать турнир"
            >
              <i class="fa fa-times"></i>
            </b-button>
          </div>
          <b-list-group v-if="showTournamentDropdown && filteredTournaments.length" class="tournament-dropdown mt-1">
            <b-list-group-item
              v-for="t in filteredTournaments"
              :key="t.id"
              button
              @click="selectTournament(t)"
              class="py-2"
            >
              <span class="font-weight-bold">{{ t.name }}</span>
              <small class="text-muted ml-2">ID: {{ t.id }}</small>
            </b-list-group-item>
          </b-list-group>
          <div v-if="currentWatch.tournament_id" class="mt-2">
            <b-badge variant="primary" class="p-2">
              Привязан к турниру: {{ selectedTournamentName }} (ID: {{ currentWatch.tournament_id }})
            </b-badge>
          </div>
          <small class="form-text text-muted">
            При привязке к турниру на странице стрима будет отображаться чат этого турнира.
          </small>
        </div>

        <div class="form-group">
          <label class="font-weight-bold">Привязка к голосованию</label>
          <div class="d-flex align-items-center">
            <b-form-select
              v-model="currentWatch.vote_id"
              :options="voteOptions"
            ></b-form-select>
            <b-button
              v-if="currentWatch.vote_id"
              variant="outline-danger"
              size="sm"
              class="ml-2"
              @click="currentWatch.vote_id = null"
              title="Отвязать голосование"
            >
              <i class="fa fa-times"></i>
            </b-button>
          </div>
          <div v-if="currentWatch.vote_id" class="mt-2">
            <b-badge variant="success" class="p-2">
              Привязано голосование ID: {{ currentWatch.vote_id }}
            </b-badge>
          </div>
        </div>

        <b-tabs class="mt-3">
          <b-tab v-for="lang in languages" :key="lang.id" :title="lang.language.toUpperCase()">
            <div class="mt-3">
              <label class="font-weight-bold">Название</label>
              <b-form-input
                v-model="currentWatch.title[lang.language.toLowerCase()]"
                :placeholder="`Название ${lang.language.toUpperCase()}`"
              ></b-form-input>
            </div>

            <div class="mt-3">
              <label class="font-weight-bold">Meta Description {{ lang.language.toUpperCase() }}</label>
              <b-form-input
                v-model="currentWatch.meta_description[lang.language.toLowerCase()]"
                :placeholder="`Meta Description ${lang.language.toUpperCase()}`"
              ></b-form-input>
            </div>
          </b-tab>
        </b-tabs>

        <div class="mt-4">
          <b-button variant="primary" type="submit" :disabled="!isFormValid">
            Сохранить изменения
          </b-button>
        </div>
      </b-form>
    </b-card>

    <b-card v-if="currentWatch.tournament_id" class="mt-4">
      <h5 class="mb-3">
        Чат турнира
        <small class="text-muted">({{ selectedTournamentName }})</small>
      </h5>
      <p class="text-muted">
        Чат привязанного турнира (ID: {{ currentWatch.tournament_id }}) отображается на странице стрима у клиента.
      </p>
      <b-alert show variant="info">
        <i class="fa fa-info-circle mr-1"></i>
        Чат этого турнира автоматически отображается на клиентской странице трансляции.
        Модерация чата доступна в разделе управления турниром.
      </b-alert>
    </b-card>
  </div>
</template>

<script>
import {mapState, mapActions} from 'vuex';
import {LanguageAdapterMixin} from "@/mixins/Language/LanguageAdapterMixin";
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import api from '@/api/api';

export default {
  name: "WatchStreams",
  components: {},
  mixins: [LanguageAdapterMixin],
  data() {
    return {
      currentWatch: {
        youtube_link: '',
        twitch_link: '',
        tournament_id: null,
        vote_id: null,
        title: {ru: '', en: '', uz: '', kk: ''},
        meta_description: {ru: '', en: '', uz: '', kk: ''},
        description: {ru: '', en: '', uz: '', kk: ''},
        is_main_banner: true
      },
      tournamentSearch: '',
      tournaments: [],
      votes: [],
      showTournamentDropdown: false,
      selectedTournamentName: '',
      editor: ClassicEditor,
      editorConfig: {
        toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote'],
        language: 'ru',
        height: '400px'
      }
    }
  },
  computed: {
    ...mapState('moduleWatch', ['mainBanner']),
    isYoutubeLinkValid() {
      const regex = /^(https?:\/\/)?(www\.youtube\.com|youtu\.?be)\/.+$/;
      return regex.test(this.currentWatch.youtube_link);
    },
    isTwitchLinkValid() {
      if (!this.currentWatch.twitch_link) return true;
      const regex = /^(https?:\/\/)?(www\.)?twitch\.tv\/.+$/;
      return regex.test(this.currentWatch.twitch_link);
    },
    twitchValidationState() {
      if (!this.currentWatch.twitch_link) return null;
      return this.isTwitchLinkValid;
    },
    isFormValid() {
      return this.isYoutubeLinkValid && this.isTwitchLinkValid;
    },
    voteOptions() {
      return [
        {value: null, text: 'Без голосования'},
        ...this.votes.map(v => ({value: v.id, text: v.title?.ru || `Голосование #${v.id}`}))
      ];
    },
    filteredTournaments() {
      if (!this.tournamentSearch) return [];
      return this.tournaments.slice(0, 10);
    }
  },
  methods: {
    ...mapActions('moduleWatch', ['fetchWatches', 'setMainBanner', 'updateWatch']),
    async loadTournaments(search = '') {
      try {
        const response = await api.tournament.index({page: 1, search});
        this.tournaments = response.data.tournaments || [];
      } catch (error) {
        console.error('Error loading tournaments:', error);
      }
    },
    async loadVotes() {
      try {
        const response = await api.votes.index();
        this.votes = response.data.data || response.data || [];
      } catch (error) {
        console.error('Error loading votes:', error);
      }
    },
    onTournamentSearch() {
      this.showTournamentDropdown = this.tournamentSearch.length > 0;
      if (this.tournamentSearch.length >= 2) {
        this.loadTournaments(this.tournamentSearch);
      }
    },
    selectTournament(tournament) {
      this.currentWatch.tournament_id = tournament.id;
      this.selectedTournamentName = tournament.name;
      this.tournamentSearch = '';
      this.showTournamentDropdown = false;
    },
    unlinkTournament() {
      this.currentWatch.tournament_id = null;
      this.selectedTournamentName = '';
      this.tournamentSearch = '';
    },
    async saveMainBanner() {
      if (!this.isFormValid) {
        this.$toast.error('Проверьте правильность ссылок', {position: 'top-right'});
        return;
      }

      try {
        if (this.mainBanner && this.mainBanner.id) {
          await this.updateWatch({id: this.mainBanner.id, watchData: this.currentWatch});
          this.$toast.success('Информация обновлена', {position: 'top-right', timeout: 2000});
        } else {
          await this.setMainBanner(this.currentWatch);
          this.$toast.success('Основной баннер создан', {position: 'top-right', timeout: 2000});
        }
      } catch (error) {
        console.error('Error saving main banner:', error);
      }
    },
    populateMainBanner() {
      if (this.mainBanner) {
        this.currentWatch.youtube_link = this.mainBanner.youtube_link || '';
        this.currentWatch.twitch_link = this.mainBanner.twitch_link || '';
        this.currentWatch.tournament_id = this.mainBanner.tournament_id || this.mainBanner.tournament?.id || null;
        this.currentWatch.vote_id = this.mainBanner.vote_id || this.mainBanner.vote?.id || null;
        this.currentWatch.title = this.mainBanner.title || {ru: '', en: '', uz: '', kk: ''};
        this.currentWatch.meta_description = this.mainBanner.meta_description || {ru: '', en: '', uz: '', kk: ''};
        this.currentWatch.description = this.mainBanner.description || {ru: '', en: '', uz: '', kk: ''};

        if (this.currentWatch.tournament_id) {
          const t = this.tournaments.find(t => t.id === this.currentWatch.tournament_id);
          this.selectedTournamentName = t?.name || this.mainBanner.tournament?.name || `Турнир #${this.currentWatch.tournament_id}`;
        }
      }
    },
  },
  created() {
    this.loadTournaments();
    this.loadVotes();
    this.fetchWatches().then(() => {
      this.populateMainBanner();
    });
    this.grabLanguages();
  },
  watch: {
    mainBanner(newBanner) {
      if (newBanner) {
        this.populateMainBanner();
      }
    }
  }
}
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
}

.tournament-dropdown {
  max-height: 250px;
  overflow-y: auto;
  position: absolute;
  z-index: 100;
  width: calc(100% - 30px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.tournament-dropdown .list-group-item:hover {
  background-color: #f0f4ff;
}
</style>
<style>
.ck-editor__editable {
  min-height: 300px;
}
</style>